import { useState } from 'react'
import { Phone } from 'lucide-react'
import { PHONE, TIERS, PROMO } from '../config/pricing'

/*
 * Single-page marketing site. Its only job is to make people call.
 * Voice, palette, and the hard don'ts live in marketing/brand.md.
 *
 * Rendered without the app's Navbar/Footer (see App.jsx) — auth, dashboard
 * and admin stay routed for the eventual customer portal, but nothing on
 * this page links into them. A visitor who calls is worth roughly ten of a
 * visitor who makes an account.
 */

const CallButton = ({ className = '', size = 'lg', label = 'Call' }) => (
  <a
    href={PHONE.href}
    className={`
      inline-flex items-center justify-center gap-3 vx-slab
      bg-vx-yellow text-vx-ink border-4 border-vx-ink
      shadow-[6px_6px_0_0_#0b0b0b] hover:shadow-[3px_3px_0_0_#0b0b0b]
      hover:translate-x-[3px] hover:translate-y-[3px]
      active:translate-x-[6px] active:translate-y-[6px] active:shadow-none
      transition-all duration-75
      focus:outline-none focus-visible:ring-4 focus-visible:ring-vx-red
      ${size === 'lg' ? 'text-3xl sm:text-4xl px-7 py-5' : 'text-xl px-5 py-3'}
      ${className}
    `}
  >
    <Phone className={size === 'lg' ? 'w-8 h-8' : 'w-5 h-5'} strokeWidth={3} aria-hidden="true" />
    <span className="sr-only">{label} </span>
    {PHONE.display}
  </a>
)

const SectionTitle = ({ children, className = '' }) => (
  <h2 className={`vx-slab vx-shadow text-4xl sm:text-6xl text-vx-yellow ${className}`}>
    {children}
  </h2>
)

/*
 * Drop either asset into public/ and this fills in automatically:
 *   public/voxerface-44s-square.mp4  — the 44s Marketplace film
 *   public/voxerface-still.jpg       — the listing still, used as poster
 * With neither present the hero collapses to a single centred column rather
 * than holding an empty box open.
 */
const HeroMedia = ({ onUnavailable }) => (
  <div className="relative border-4 border-vx-ink bg-black shadow-[10px_10px_0_0_#0b0b0b] vx-scanlines overflow-hidden">
    <video
      className="w-full aspect-square object-cover"
      src="/voxerface-44s-square.mp4"
      poster="/voxerface-still.jpg"
      controls
      playsInline
      muted
      loop
      preload="metadata"
      onError={onUnavailable}
    />
  </div>
)

const CAPABILITIES = [
  {
    title: 'Answers 24/7',
    body: 'Nights, weekends, mid-job, mid-haircut, mid-nap. It picks up when you can’t.',
  },
  {
    title: 'Books appointments & makes calls',
    body: 'Not just a notepad. It puts people on your calendar and calls them back.',
  },
  {
    title: 'Texts you the lead',
    body: 'Name, number, what they need. In your pocket before you’re off the ladder.',
  },
]

const FAQS = [
  {
    q: 'Do I need a new phone number?',
    a: 'No. You keep the number you already have — we just forward it. Nothing changes on your truck, your sign, or your Google listing.',
  },
  {
    q: 'What is the $29 a month for?',
    a: 'It covers the phone line, the AI, and the lead texts. Roughly 100 calls a month. No contract. For comparison, a human answering service runs $100–300 a month.',
  },
  {
    q: 'What happens if I cancel?',
    a: 'You turn off forwarding. Your number keeps working exactly the way it does today. There is nothing to undo.',
  },
  {
    q: 'How long does setup take?',
    a: 'Usually same day, once we know how you want your phone answered. In Austin, we come to you. Anywhere else, we set it up remotely.',
  },
  {
    q: 'Will my customers know it’s an AI?',
    a: 'It’s upfront about being an assistant, and it’s built to sound like your business — your greeting, your hours, your services. Call the number above and hear it yourself. That’s the demo.',
  },
]

const LandingView = () => {
  const [hasMedia, setHasMedia] = useState(true)

  return (
    <div className="min-h-screen overflow-x-hidden bg-vx-ink text-vx-paper font-sans pb-24 md:pb-0">
      {/* ---- Top call bar ---- */}
      <div className="sticky top-0 z-40 bg-vx-red border-b-4 border-vx-ink">
        <div className="max-w-6xl mx-auto px-4 py-2 flex items-center justify-between gap-4">
          <span className="vx-slab text-lg sm:text-2xl text-white">
            Call now
            <span className="hidden sm:inline"> — talk to our AI receptionist</span>
          </span>
          <a
            href={PHONE.href}
            className="vx-slab text-lg sm:text-2xl text-vx-yellow whitespace-nowrap hover:underline"
          >
            {PHONE.display}
          </a>
        </div>
      </div>

      {/* ---- Hero ---- */}
      <header className="relative overflow-hidden vx-scanlines vx-tracking border-b-4 border-vx-yellow">
        <div
          className={`relative max-w-6xl mx-auto px-4 py-14 sm:py-20 grid gap-12 items-center ${
            hasMedia ? 'md:grid-cols-2' : 'max-w-3xl text-center justify-items-center'
          }`}
        >
          <div className={hasMedia ? '' : 'flex flex-col items-center'}>
            <p className="vx-slab text-2xl sm:text-3xl text-vx-yellow vx-chroma vx-slam">
              AI Receptionists
            </p>
            <h1 className="vx-slab vx-shadow text-5xl sm:text-7xl mt-2 text-white vx-slam">
              Never miss a<br />call again!
            </h1>

            <p className="mt-6 text-lg sm:text-xl text-vx-paper/90 max-w-md">
              Answers 24/7. Books appointments. Texts you the lead — name,
              number, what they need.
            </p>

            <div className="mt-8 vx-flash">
              <CallButton />
            </div>
            <p className="mt-3 text-sm text-vx-paper/70">
              You’ll be talking to the actual product. That’s the demo.
            </p>

            <div className="mt-8 inline-block bg-vx-yellow text-vx-ink px-5 py-3 border-4 border-vx-ink shadow-[6px_6px_0_0_#0b0b0b]">
              <span className="vx-slab text-2xl sm:text-3xl">
                Starting at only $69!
              </span>
              <span className="block text-[11px] font-semibold tracking-wide mt-1">
                Plus $29/mo. That’s it. We promise.
              </span>
            </div>
          </div>

          {hasMedia && <HeroMedia onUnavailable={() => setHasMedia(false)} />}
        </div>
      </header>

      {/* ---- Capabilities ---- */}
      <section className="max-w-6xl mx-auto px-4 py-16">
        <SectionTitle>What it does</SectionTitle>
        <div className="mt-10 grid md:grid-cols-3 gap-6">
          {CAPABILITIES.map((c) => (
            <div
              key={c.title}
              className="bg-vx-paper text-vx-ink border-4 border-vx-ink p-6 shadow-[8px_8px_0_0_#FFE500]"
            >
              <h3 className="vx-slab text-2xl sm:text-3xl">{c.title}</h3>
              <p className="mt-3 text-base leading-relaxed">{c.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ---- Keep your number ---- */}
      <section className="bg-vx-yellow text-vx-ink border-y-4 border-vx-ink">
        <div className="max-w-6xl mx-auto px-4 py-12 text-center">
          <h2 className="vx-slab text-4xl sm:text-5xl">You keep your number</h2>
          <p className="mt-4 text-lg max-w-2xl mx-auto font-medium">
            We forward the line you already have. Nothing changes on your truck,
            your sign, or your Google listing — and you can turn it off any time.
          </p>
        </div>
      </section>

      {/* ---- Pricing ---- */}
      <section className="max-w-6xl mx-auto px-4 py-16">
        <SectionTitle>What it costs</SectionTitle>
        <div className="mt-10 grid md:grid-cols-2 gap-6">
          {TIERS.map((tier) => (
            <div
              key={tier.id}
              className={`
                bg-vx-paper text-vx-ink border-4 border-vx-ink p-7 flex flex-col
                ${tier.popular ? 'shadow-[10px_10px_0_0_#D42A1F]' : 'shadow-[10px_10px_0_0_#FFE500]'}
              `}
            >
              <h3 className="vx-slab text-4xl">{tier.name}</h3>

              <div className="mt-4 flex flex-wrap items-baseline gap-x-3 gap-y-1">
                <span className="vx-slab text-5xl">{tier.setup}</span>
                <span className="text-sm font-semibold uppercase tracking-wide">
                  {tier.setupNote}
                </span>
              </div>
              <div className="mt-1 flex flex-wrap items-baseline gap-x-3 gap-y-1">
                <span className="vx-slab text-3xl">{tier.monthly}</span>
                <span className="text-sm font-semibold uppercase tracking-wide">
                  {tier.monthlyNote}
                </span>
              </div>

              <ul className="mt-6 space-y-2 flex-1">
                {tier.features.map((f) => (
                  <li key={f} className="flex gap-3 text-base">
                    <span aria-hidden="true" className="font-bold">✓</span>
                    <span>{f}</span>
                  </li>
                ))}
              </ul>

              <div className="mt-7">
                <p className="text-sm font-bold uppercase tracking-wide mb-2">
                  {tier.cta}
                </p>
                <CallButton size="sm" label={tier.cta} />
              </div>
            </div>
          ))}
        </div>

        <p className="mt-8 text-center text-lg text-vx-paper/80">
          For comparison: a human answering service runs{' '}
          <strong className="text-vx-yellow">$100–300 a month.</strong>
        </p>
      </section>

      {/* ---- Austin ---- */}
      <section className="bg-vx-navy border-y-4 border-vx-ink">
        <div className="max-w-6xl mx-auto px-4 py-12 text-center">
          <h2 className="vx-slab vx-shadow text-4xl sm:text-5xl text-vx-yellow">
            Austin businesses: we come to you for setup.
          </h2>
          <p className="mt-4 text-lg text-white/90">
            Outside Austin? Same-day remote setup, no problem.
          </p>
        </div>
      </section>

      {/* ---- Promo ---- */}
      <section className="max-w-6xl mx-auto px-4 py-16">
        <div className="bg-vx-yellow text-vx-ink border-4 border-vx-ink p-8 sm:p-10 text-center shadow-[12px_12px_0_0_#D42A1F]">
          <p className="vx-slab text-2xl sm:text-3xl">★ {PROMO.deadline} ★</p>
          <h2 className="vx-slab text-4xl sm:text-6xl mt-2">{PROMO.headline}</h2>
          <p className="mt-4 max-w-2xl mx-auto text-lg font-medium">{PROMO.body}</p>
          <div className="mt-8">
            <CallButton />
          </div>
        </div>
      </section>

      {/* ---- FAQ ---- */}
      <section className="max-w-3xl mx-auto px-4 py-16">
        <SectionTitle>Questions</SectionTitle>
        <dl className="mt-10 space-y-8">
          {FAQS.map((f) => (
            <div key={f.q} className="border-l-4 border-vx-yellow pl-5">
              <dt className="vx-slab text-2xl text-vx-yellow">{f.q}</dt>
              <dd className="mt-2 text-base leading-relaxed text-vx-paper/90">{f.a}</dd>
            </div>
          ))}
        </dl>
      </section>

      {/* ---- Footer ---- */}
      <footer className="border-t-4 border-vx-yellow">
        <div className="max-w-6xl mx-auto px-4 py-12 text-center">
          <p className="vx-slab text-3xl text-vx-yellow">voxerface.com</p>
          <div className="mt-6">
            <CallButton />
          </div>
          <p className="mt-8 text-xs text-vx-paper/45">
            It sells you an AI receptionist. Meta. We know.
          </p>
          <p className="mt-3 text-xs text-vx-paper/40">
            © {new Date().getFullYear()} Voxerface · Austin, TX
          </p>
        </div>
      </footer>

      {/* ---- Mobile sticky call bar ---- */}
      <div className="md:hidden fixed bottom-0 left-0 right-0 z-50 bg-vx-red border-t-4 border-vx-ink safe-area-bottom">
        <a
          href={PHONE.href}
          className="flex items-center justify-center gap-3 py-4 vx-slab text-2xl text-vx-yellow"
        >
          <Phone className="w-6 h-6" strokeWidth={3} aria-hidden="true" />
          Call {PHONE.display}
        </a>
      </div>
    </div>
  )
}

export default LandingView
